import { useState, useEffect, useRef } from 'react';
import { TextInput, Select, NumberInput, Box, Text, Group, ActionIcon } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { z } from 'zod';
import {
  LicenceTypeSchema,
  LicenceStatusSchema,
  LicenceRecord,
} from '@lms/shared';

interface InlineEditorProps {
  field: keyof LicenceRecord;
  value: any;
  editable: boolean;
  onSave: (newValue: any) => Promise<void>;
  onCancel?: () => void;
  customDisplay?: React.ReactNode;
}

/**
 * Get the appropriate Zod schema for a specific field
 */
function getFieldSchema(field: keyof LicenceRecord): z.ZodType<any> {
  switch (field) {
    case 'licence_type':
      return LicenceTypeSchema;
    case 'status':
      return LicenceStatusSchema;
    case 'max_accounts':
    case 'current_account_count':
      return z.number().int().positive();
    case 'valid_through':
    case 'session_expiry':
      return z.string();
    case 'registration_email':
      return z.string().email();
    case 'customer_key':
    case 'purchaser_aws_account_id':
    case 'hub_aws_account_id':
    case 'product_code':
    case 'session_token':
    case 'version':
    case 'last_updated':
    case 'customer_identifier':
      return z.string();
    default:
      return z.any();
  }
}

/**
 * InlineEditor component for editing licence record fields
 * Provides field-specific input types with validation
 */
export function InlineEditor({
  field,
  value,
  editable,
  onSave,
  onCancel,
  customDisplay,
}: InlineEditorProps) {
  const [editValue, setEditValue] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset edit value when value prop changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (editable) {
      setIsEditing(true);
      setError(null);
    }
  };

  const handleSave = async () => {
    // Validate input using Zod schema
    const schema = getFieldSchema(field);
    const validation = schema.safeParse(editValue);

    if (!validation.success) {
      // Display validation error inline
      const errorMessage = validation.error.errors[0]?.message || 'Invalid input';
      setError(errorMessage);
      return;
    }

    try {
      await onSave(editValue);
      setIsEditing(false);
      setError(null);
    } catch (err) {
      // Parent component handles the notification via centralized error handler
      // Keep editing mode open so user can correct the value
      // Show inline error for immediate feedback
      const errorMessage = err instanceof Error ? err.message : 'Failed to save';
      setError(errorMessage);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setError(null);
    setIsEditing(false);
    onCancel?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  // Render read-only view
  if (!isEditing) {
    return (
      <Box
        onClick={handleClick}
        style={{
          cursor: editable ? 'pointer' : 'default',
          padding: '6px 10px',
          borderRadius: 'var(--mantine-radius-sm)',
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: editable ? 'transparent' : undefined,
          transition: 'all 0.2s ease',
          border: '1px solid transparent',
          position: 'relative',
        }}
        onMouseEnter={(e) => {
          if (editable) {
            e.currentTarget.style.backgroundColor = 'var(--mantine-color-blue-0)';
            e.currentTarget.style.borderColor = 'var(--mantine-color-blue-2)';
          }
        }}
        onMouseLeave={(e) => {
          if (editable) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = 'transparent';
          }
        }}
        onFocus={(e) => {
          if (editable) {
            e.currentTarget.style.outline = 'none';
          }
        }}
        onBlur={(e) => {
          if (editable) {
            e.currentTarget.style.outline = 'none';
          }
        }}
        tabIndex={editable ? 0 : -1}
        role={editable ? 'button' : undefined}
        aria-label={editable ? `Edit ${field}` : undefined}
      >
        {customDisplay || (
          <Text
            size="sm"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value ?? '-'}
          </Text>
        )}
      </Box>
    );
  }

  // Render appropriate editor based on field type
  const renderEditor = () => {
    switch (field) {
      case 'status':
        return (
          <Select
            value={editValue}
            onChange={(val) => setEditValue(val)}
            data={[
              { value: 'ACTIVE', label: 'Active' },
              { value: 'INACTIVE', label: 'Inactive' },
              { value: 'PENDING', label: 'Pending' },
              { value: 'ACTIVE_NO_HUB_ACCOUNT_ID', label: 'Active (No Hub Account)' },
            ]}
            error={error}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        );

      case 'licence_type':
        return (
          <Select
            value={editValue}
            onChange={(val) => setEditValue(val)}
            data={[
              { value: 'MARKETPLACE', label: 'Marketplace' },
              { value: 'PRIVATE', label: 'Private' },
            ]}
            error={error}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        );

      case 'max_accounts':
      case 'current_account_count':
        return (
          <NumberInput
            value={editValue}
            onChange={(val) => setEditValue(val)}
            error={error}
            onKeyDown={handleKeyDown}
            min={1}
            step={1}
            autoFocus
          />
        );

      case 'valid_through':
      case 'session_expiry': {
        const dateValue = editValue ? new Date(editValue) : null;
        return (
          <input
            type="datetime-local"
            value={dateValue ? dateValue.toISOString().slice(0, 16) : ''}
            onChange={(e) => {
              if (e.target.value) {
                setEditValue(new Date(e.target.value).toISOString());
              } else {
                setEditValue(null);
              }
            }}
            onKeyDown={handleKeyDown}
            autoFocus
            style={{
              padding: '8px',
              border: error ? '1px solid red' : '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
        );
      }

      default:
        return (
          <TextInput
            ref={inputRef}
            value={editValue ?? ''}
            onChange={(event) => setEditValue(event.currentTarget.value)}
            error={error}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        );
    }
  };

  return (
    <Box>
      <Group gap="xs" wrap="nowrap">
        {renderEditor()}
        <ActionIcon
          color="green"
          variant="filled"
          onClick={handleSave}
          size="sm"
          aria-label="Save"
          title="Save (Enter)"
        >
          <IconCheck size={16} />
        </ActionIcon>
        <ActionIcon
          color="red"
          variant="filled"
          onClick={handleCancel}
          size="sm"
          aria-label="Cancel"
          title="Cancel (Esc)"
        >
          <IconX size={16} />
        </ActionIcon>
      </Group>
      {error && (
        <Text size="xs" c="red" mt={4}>
          {error}
        </Text>
      )}
    </Box>
  );
}
